import {Component, computed, input, ChangeDetectionStrategy} from "@angular/core"
import {ListItemComponent} from "@app/common/components/item"
import {hasChildren} from "@app/template-editor/components/template-tree-scene-node-info-tree/template-tree-scene-node-info-tree-types"
import type {SceneNodeInfoTreeItem} from "@app/template-editor/components/template-tree-scene-node-info-tree/template-tree-scene-node-info-tree-types"
import type {TemplateTreeSceneNodeInfoTreeComponent} from "@app/template-editor/components/template-tree-scene-node-info-tree/template-tree-scene-node-info-tree.component"
import {getTemplateNodeClassLabel, getTemplateNodeLabel, SceneNodes} from "@cm/template-nodes"
import {MatTooltipModule} from "@angular/material/tooltip"
import {getNodeIconClass, getNodeIconSecondaryClass} from "@app/template-editor/helpers/template-icons"
import {TemplateTreeSceneNodeAggregationComponent} from "@app/template-editor/components/template-tree-scene-node-aggregation/template-tree-scene-node-aggregation.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree-scene-node-info-tree-item",
    imports: [ListItemComponent, MatTooltipModule, TemplateTreeSceneNodeAggregationComponent],
    templateUrl: "./template-tree-scene-node-info-tree-item.component.html",
    styleUrls: ["./template-tree-scene-node-info-tree-item.component.scss", "./../../helpers/template-icons.scss"],
})
export class TemplateTreeSceneNodeInfoTreeItemComponent {
    readonly $treeItem = input.required<SceneNodeInfoTreeItem>({alias: "treeItem"})
    readonly $infoTree = input.required<TemplateTreeSceneNodeInfoTreeComponent>({alias: "infoTree"})
    readonly $node = computed(() => {
        const treeItem = this.$treeItem()
        if (SceneNodes.isSceneNode(treeItem)) {
            return undefined
        }
        return treeItem.templateNode
    })
    readonly $expandable = computed(() => hasChildren(this.$treeItem()))
    readonly $nodeIconClass = computed(() => {
        const treeItem = this.$treeItem()
        if (SceneNodes.isSceneNode(treeItem)) {
            if (SceneNodes.DataObjectReference.is(treeItem)) {
                if (treeItem.dataObject.mediaType.startsWith("image/")) return "far fa-image"
                return "far fa-binary"
            }
            return "far fa-circle"
        }

        return getNodeIconClass(treeItem.templateNode.nodeClass)
    })
    readonly $secondaryNodeIconClass = computed(() => {
        const node = this.$node()
        if (!node) return null
        return getNodeIconSecondaryClass(node.nodeClass)
    })
    readonly $tooltip = computed(() => {
        const treeItem = this.$treeItem()
        if (SceneNodes.isSceneNode(treeItem)) {
            return treeItem.id
        }
        return getTemplateNodeClassLabel(treeItem.templateNode)
    })
    readonly $aggregatedSceneNodes = computed(() => {
        const sceneNodes = new Set<SceneNodes.SceneNode>()
        const infoNode = this.$treeItem()
        const collectSceneNodes = (node: SceneNodeInfoTreeItem) => {
            if (SceneNodes.isSceneNode(node)) {
                sceneNodes.add(node)
                return
            }

            for (const childNode of node.children) {
                if (SceneNodes.isSceneNode(childNode)) sceneNodes.add(childNode)
                else collectSceneNodes(childNode)
            }
        }
        collectSceneNodes(infoNode)
        return Array.from(sceneNodes)
    })
    readonly $label = computed(() => {
        const treeItem = this.$treeItem()
        if (SceneNodes.isSceneNode(treeItem)) {
            if (SceneNodes.DataObjectReference.is(treeItem)) {
                const {dataObject} = treeItem
                return dataObject.originalFileName
            }

            const {id} = treeItem
            const colonIndex = id.lastIndexOf(":")
            if (colonIndex === -1) return id
            return id.slice(colonIndex + 1)
        }

        return getTemplateNodeLabel(treeItem.templateNode)
    })
}
